/**
 * DeleteListingDialog — a seller deleting a draft or withdrawn listing (all
 * states; legal copy §15.3, `GEN.listing.delete.*`).
 *
 * Before anything is deleted it loads what the delete will do
 * (`GET /api/houses/{id}/delete-preview`) and says so in legal's words: the
 * pending offers that will be closed (`.offers.one` / `.offers.other`), a
 * counter-offer that will be withdrawn, the tours that will be cancelled,
 * that the listing agreement isn't ended (product decision: `.unchanged`),
 * and that signed records are kept. The seller must tick the checkbox.
 *
 * The server re-checks everything. Its 409 refusals show legal's
 * `GEN.error.listing_delete_*` text, or `WI.error.delete_blocked_by_notices`
 * through deleteBlockedMessage. `listing_delete_changed` (a buyer answered an
 * offer while the seller was deleting) reloads the preview so the dialog says
 * what is true now.
 *
 * Contract: src/agents/listingDeleteAgent.js.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import { deleteListing, getListingDeletePreview, LISTING_DELETE_CHANGED } from '../../agents/listingDeleteAgent';
import { deleteBlockedMessage } from '../ContingencyHub/contract/contractFormat';
import { LISTING_AGREEMENT_KEY, listingDeleteCopy, openOffersText } from './listingDeleteCopy';
import styles from './DeleteListingDialog.module.css';

/** 409 codes → legal's `GEN.error.listing_delete_*` key (copy §15.3 rule 2). */
export const DELETE_REFUSAL_KEYS = {
  listing_not_off_market: 'GEN.error.listing_delete_not_off_market',
  listing_delete_not_off_market: 'GEN.error.listing_delete_not_off_market',
  listing_has_transaction: 'GEN.error.listing_delete_has_transaction',
  // A pending or sold listing: §15.3 gives it the has-transaction message.
  listing_pending_or_sold: 'GEN.error.listing_delete_has_transaction',
  listing_has_accepted_offer: 'GEN.error.listing_delete_has_transaction',
  listing_has_signed_documents: 'GEN.error.listing_delete_signed_documents',
  [LISTING_DELETE_CHANGED]: 'GEN.error.listing_delete_changed',
};

const GENERIC_FAILURE = "This listing couldn't be deleted. Nothing was changed. Please try again.";

const detailOf = (err) => (err && typeof err.detail === 'object' && err.detail !== null && !Array.isArray(err.detail)
  ? err.detail : null);

/**
 * Legal's message for a refused delete: the server's `GEN.error.listing_delete_*`
 * copy key, else the one for its code, else `WI.error.delete_blocked_by_notices`
 * for 409 `notice_evidence_retained`, else a plain line.
 *
 * @param {Error & {status?: number, detail?: any}} err - An APIAgent error.
 * @returns {string}
 */
export function listingDeleteErrorMessage(err) {
  const detail = detailOf(err);
  const fromKey = typeof detail?.copy_key === 'string' && detail.copy_key.startsWith('GEN.error.listing_delete_')
    ? listingDeleteCopy(detail.copy_key) : null;
  if (fromKey) return fromKey;
  const key = DELETE_REFUSAL_KEYS[detail?.code];
  if (key) return listingDeleteCopy(key);
  const blocked = deleteBlockedMessage(err);
  if (blocked) return blocked;
  if (err?.status === 403) return 'Only the seller of this listing can delete it.';
  if (err?.status === 404) return 'This listing was not found. It may already have been deleted.';
  return GENERIC_FAILURE;
}

/** True when the listing changed while the seller was deleting it. */
export function isListingDeleteChanged(err) {
  const detail = detailOf(err);
  return detail?.code === LISTING_DELETE_CHANGED || detail?.copy_key === 'GEN.error.listing_delete_changed';
}

/** "123 Main St, Austin, TX 78701" from a listing. */
export function listingAddress(house) {
  if (!house) return '';
  const cityLine = [house.city, [house.state, house.zip_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [house.address, cityLine].filter(Boolean).join(', ');
}

/** The "Delete listing" card action (copy §15.3; draft and withdrawn listings only). */
export function DeleteListingButton({ house, onClick }) {
  return (
    <button
      type="button"
      className={styles.deleteAction}
      onClick={() => onClick(house)}
      aria-haspopup="dialog"
      data-testid={`delete-listing-${house.id}`}
    >
      {listingDeleteCopy('GEN.listing.delete.action')}
    </button>
  );
}

/** Legal's `GEN.listing.delete.done`, announced politely after a delete. */
export function ListingDeletedNotice({ children }) {
  return <p className={styles.deletedNotice} role="status" data-testid="listing-deleted-notice">{children}</p>;
}

/**
 * @param {object} props
 * @param {object} props.house - The listing (`id`, `address`, `city`, `state`, `zip_code`).
 * @param {Function} props.onClose
 * @param {Function} props.onDeleted - `(houseId, result)` once the server deleted it.
 */
export default function DeleteListingDialog({ house, onClose, onDeleted }) {
  const uid = useId();
  const cancelRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setPreview(await getListingDeletePreview(house.id));
    } catch (err) {
      setPreview(null);
      setLoadError(detailOf(err)?.code || detailOf(err)?.copy_key
        ? listingDeleteErrorMessage(err)
        : "We couldn't check what deleting this listing would do. Nothing was changed.");
    } finally {
      setLoading(false);
    }
  }, [house.id]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const refusalText = preview && !preview.allowed
    ? listingDeleteErrorMessage({ status: 409, detail: preview.refusal || {} })
    : null;
  const ready = Boolean(preview) && preview.allowed && !loading;
  const canDelete = ready && confirmed && !deleting;
  const address = preview?.propertyAddress || listingAddress(house);
  const offersText = ready ? openOffersText(preview.openOfferCount) : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!canDelete) return;
    setDeleting(true);
    setError(null);
    try {
      const result = await deleteListing(house.id);
      onDeleted(house.id, result);
    } catch (err) {
      setError(listingDeleteErrorMessage(err));
      if (isListingDeleteChanged(err)) {
        // Say what is true now, and ask again.
        setConfirmed(false);
        loadPreview();
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      open
      onClose={deleting ? () => {} : onClose}
      title={listingDeleteCopy('GEN.listing.delete.confirm.title')}
      size="md"
      initialFocusRef={cancelRef}
      dismissOnOverlayClick={false}
    >
      <form onSubmit={submit} className={styles.form} data-testid="delete-listing-dialog" noValidate>
        <p className={styles.body} data-testid="delete-listing-body">
          {listingDeleteCopy('GEN.listing.delete.confirm.body', { property_address: address })}
        </p>

        {loading && (
          <p className={styles.muted} role="status" data-testid="delete-listing-loading">
            Checking what deleting this listing will do…
          </p>
        )}
        {loadError && (
          <div className={styles.error} role="alert" data-testid="delete-listing-load-error">
            <span>{loadError}</span>{' '}
            <button type="button" className={styles.linkBtn} onClick={loadPreview}>Try again</button>
          </div>
        )}
        {refusalText && (
          <p className={styles.error} role="alert" data-testid="delete-listing-refusal">{refusalText}</p>
        )}

        {ready && (
          <>
            {(offersText || preview.hasCounter || preview.tourCount > 0) && (
              <ul className={styles.effects} aria-label="What else happens" data-testid="delete-listing-effects">
                {offersText && <li data-testid="delete-listing-offers">{offersText}</li>}
                {preview.hasCounter && (
                  <li data-testid="delete-listing-counter">{listingDeleteCopy('GEN.listing.delete.confirm.counter')}</li>
                )}
                {preview.tourCount > 0 && (
                  <li data-testid="delete-listing-tours">{listingDeleteCopy('GEN.listing.delete.confirm.tours')}</li>
                )}
              </ul>
            )}
            {preview.listingAgreementSigned && (
              <p className={styles.note} data-testid="delete-listing-agreement">{listingDeleteCopy(LISTING_AGREEMENT_KEY)}</p>
            )}
            <p className={styles.note} data-testid="delete-listing-records">
              {listingDeleteCopy('GEN.listing.delete.confirm.records')}
            </p>

            <label className={styles.check} htmlFor={`${uid}-confirm`}>
              <input
                id={`${uid}-confirm`}
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={deleting}
                data-testid="delete-listing-checkbox"
              />
              <span>{listingDeleteCopy('GEN.listing.delete.confirm.checkbox')}</span>
            </label>
          </>
        )}

        {error && <p className={styles.error} role="alert" data-testid="delete-listing-error">{error}</p>}

        <div className={styles.actions}>
          <button type="button" ref={cancelRef} className={styles.secondary} onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button type="submit" className={styles.danger} disabled={!canDelete} data-testid="delete-listing-confirm">
            {deleting ? 'Deleting…' : listingDeleteCopy('GEN.listing.delete.confirm.button')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
